import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiResponse } from '../../../_core/_model/api-response';
import { forkJoin, Observable } from 'rxjs';
import {
  MlCandidateGovernanceDto,
  MlCandidateGovernanceSummaryDto,
  MlCandidateShadowEvaluationStatusDto,
  MlCandidateShadowSelectionDto,
  MlNetShadowComparisonStatusDto,
  MlNetShadowComparisonSummaryDto
} from '../../../_core/_model/machine-learning';
import { AuthService } from '../../../_core/_services/auth.service';
import { MachineLearningAdminService } from '../../../_core/_services/machine-learning-admin.service';
import { NotificationService } from '../../../_core/_services/notification.service';

@Component({
  selector: 'app-admin-ml-governance',
  standalone: false,
  templateUrl: './admin-ml-governance.html',
  styleUrl: './admin-ml-governance.css'
})
export class AdminMlGovernance implements OnInit {
  loading = false;
  actionLoading = false;
  error = '';
  isSuperAdmin = false;
  summary: MlCandidateGovernanceSummaryDto | null = null;
  candidates: MlCandidateGovernanceDto[] = [];
  selection: MlCandidateShadowSelectionDto | null = null;
  shadowEvaluation: MlCandidateShadowEvaluationStatusDto | null = null;
  comparisonStatus: MlNetShadowComparisonStatusDto | null = null;
  comparisonSummary: MlNetShadowComparisonSummaryDto | null = null;
  filterStatus = '';
  selectedCandidate: MlCandidateGovernanceDto | null = null;
  action = '';
  notes = '';

  constructor(
    private mlService: MachineLearningAdminService,
    private auth: AuthService,
    private notify: NotificationService
  ) {}

  ngOnInit(): void {
    this.auth.getCurrentUser().subscribe(user => this.isSuperAdmin = user?.role === 'SuperAdmin');
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    forkJoin({
      summary: this.mlService.getLoginRiskCandidateSummary(),
      candidates: this.mlService.getLoginRiskCandidates(this.filterStatus || null, 50),
      selection: this.mlService.getLoginRiskShadowSelection(),
      evaluation: this.mlService.getLoginRiskCandidateShadowEvaluationStatus(),
      comparison: this.mlService.getLoginRiskShadowComparisonStatus()
    }).subscribe({
      next: result => {
        this.summary = result.summary.data;
        this.candidates = result.candidates.data || [];
        this.selection = result.selection.data;
        this.shadowEvaluation = result.evaluation.data;
        this.comparisonStatus = result.comparison.data;
        this.loading = false;
        const version = this.selection?.modelVersion || this.comparisonStatus?.selectedVersion;
        if (version) this.loadComparison(version);
        else this.comparisonSummary = null;
      },
      error: (error: HttpErrorResponse) => {
        this.error = this.errorMessage(error, 'Unable to load candidate governance.');
        this.loading = false;
      }
    });
  }

  openAction(candidate: MlCandidateGovernanceDto, action: string): void {
    this.selectedCandidate = candidate;
    this.action = action;
    this.notes = '';
  }

  closeAction(): void {
    this.selectedCandidate = null;
    this.action = '';
    this.notes = '';
  }

  submitCandidateAction(): void {
    if (!this.isSuperAdmin || !this.selectedCandidate) return;
    const id = this.selectedCandidate.candidateReviewId;
    if ((this.action === 'reject' || this.action === 'stop') && !this.notes.trim()) {
      this.notify.showWarning('A reason is required for this action.', 'Reason required');
      return;
    }

    this.actionLoading = true;
    const operation: Observable<ApiResponse<any>> = this.action === 'approve'
      ? this.mlService.approveLoginRiskCandidate(id, this.notes)
      : this.action === 'reject'
        ? this.mlService.rejectLoginRiskCandidate(id, this.notes)
        : this.mlService.selectLoginRiskCandidateForShadow(id, this.notes);

    operation.subscribe({
      next: response => {
        this.notify.showSuccess(response.message || 'Candidate governance updated.', 'Success');
        this.closeAction();
        this.load();
      },
      error: error => this.notify.showError(this.errorMessage(error, 'Unable to update candidate governance.'), 'Error'),
      complete: () => this.actionLoading = false
    });
  }

  stopShadow(): void {
    if (!this.isSuperAdmin || !this.notes.trim()) {
      this.notify.showWarning('Enter a reason before stopping shadow selection.', 'Reason required');
      return;
    }
    this.actionLoading = true;
    this.mlService.stopLoginRiskShadowSelection(this.notes).subscribe({
      next: response => {
        this.notify.showSuccess(response.message || 'Shadow selection stopped.', 'Success');
        this.notes = '';
        this.load();
      },
      error: error => this.notify.showError(this.errorMessage(error, 'Unable to stop shadow selection.'), 'Error'),
      complete: () => this.actionLoading = false
    });
  }

  evaluateShadow(completeIfReady: boolean): void {
    if (!this.isSuperAdmin) return;
    this.actionLoading = true;
    this.mlService.evaluateLoginRiskCandidateShadow(completeIfReady, this.notes).subscribe({
      next: response => {
        this.notify.showSuccess(response.data.message || response.message || 'Shadow evaluation completed.', 'Success');
        this.notes = '';
        this.load();
      },
      error: error => this.notify.showError(this.errorMessage(error, 'Unable to evaluate candidate shadow performance.'), 'Error'),
      complete: () => this.actionLoading = false
    });
  }

  canApprove(item: MlCandidateGovernanceDto): boolean { return item.status.toLowerCase() === 'pendingreview'; }
  canReject(item: MlCandidateGovernanceDto): boolean { return ['pendingreview', 'approvedforshadow'].includes(item.status.toLowerCase()); }
  canSelect(item: MlCandidateGovernanceDto): boolean { return item.status.toLowerCase() === 'approvedforshadow'; }

  badgeClass(value?: string | null): string {
    switch ((value || '').toLowerCase()) {
      case 'promotionready': case 'approvedforshadow': case 'stable': return 'text-bg-success';
      case 'selectedforshadow': case 'pendingreview': return 'text-bg-primary';
      case 'qualitygaterejected': case 'rejected': case 'warning': return 'text-bg-danger';
      case 'shadowcompleted': return 'text-bg-secondary';
      default: return 'text-bg-warning text-dark';
    }
  }

  private loadComparison(version: string): void {
    this.mlService.getLoginRiskShadowComparisonSummary(version).subscribe({
      next: response => this.comparisonSummary = response.data,
      error: () => this.comparisonSummary = null
    });
  }

  private errorMessage(error: any, fallback: string): string {
    return error?.error?.error?.details || error?.error?.message || error?.message || fallback;
  }
}
